import React from "react";

export const ucAppserverBaseUrl = process.env.REACT_APP_UC_APPSERVER_URL || "/api";

export const publicUrl = process.env.REACT_APP_PUBLIC_URL || "/am";

export const ucEndpoints = {
  authentication: "/authentication",
  login: "/users/login",
  accounts: "/account",
  layers: "/layers/list",
  createClientAccount: "/users/client_create",
  regions: "/regions",
  pencilerRegions: "/regions/penciler",
  users: "/users",
  get usersAccounting() {
    return `${this.users}/accounting`;
  },
  get usersLimits() {
    return `${this.users}/limits`;
  },
  get usersSettings() {
    return `${this.users}/settings`;
  },
  thisUser: "/users/this",
  inviteUser: "/users/invite",
  renewInviteUser: "/users/invite/renew",
  startPasswordReset: "/users/start_password_reset",
  finishPasswordReset: "/users/finish_password_reset",
  checkPasswordResetToken: "/users/check_password_reset_token",
  checkUsed: "/users/check_used",
  organizations: "/organizations",
  thisOrganization: "/organizations/this",
  thisOrganizationUsers: "/organizations/this/users",
  checkInvitation: "/users/check_invitation",
  checkRegistration: "/users/check_registration",
  registerAccount: "/users/register",
  checkPasswordReset: "/users/check_password_reset",
  confirmRegistration: "/users/confirm_registration",
  finishRegistration: "/users/finish_registration",
  acceptInvitation: "/users/accept_invitation",
  subscriptionOptions: "/subscription_options",
  stripeCheckoutSession: "/stripe/create_checkout_session",
  stripeProducts: "/stripe/products",
  pencilerStartProjectsStatus: "/penciler_start_projects/status",
  userInviteStatusByRole: "/users/invites/status",
  stripeProrationCostPreview: "/stripe/subscription_proration/:planId/preview",
  stripeProrationCost: "/stripe/subscription_proration/:planId",
  currentPencilerSubscription: "/subscriptions/penciler/this",
  aclList: "/acls/list",
  organizationsRoles: "/organizations/roles",
  organizationsUsers: "/organizations/users",
  organizationsContacts: "/organizations/contacts",
  organizationsAdministrators: "/organizations/administrators",
  availableRegions: "/users/this/available_regions",
  userRoles: "/users/roles",
  renewToken: "/users/renew_token",
  userTypes: "/user_types",
  publicRoles: "/roles/public",
  permissions: "/acls/permissions",
  fineGrainedPermissions: "/acls/fine_grained",
  selectedRegionSettingsPerOrganization: "/regions/this",
  selectedRegionSettingsPerOrganizationUpdate: "/regions/settings",
  selectedRegionSettingsPerUser: "/users/region/settings",
  get twoFactorAuthentication() {
    return `${this.authentication}/2fa`;
  },
  get setup2fa() {
    return `${this.twoFactorAuthentication}/setup`;
  },
  get setup2faHasSavedRecoveryCode() {
    return `${this.setup2fa}/has_saved_recovery_code`;
  },
  get disable2fa() {
    return `${this.twoFactorAuthentication}/disable`;
  },
  get recoveryCode2fa() {
    return `${this.twoFactorAuthentication}/recovery_code`;
  },
  get disable2faByRecoveryCode() {
    return `${this.recoveryCode2fa}/disable`;
  },
  get status2fa() {
    return `${this.twoFactorAuthentication}/status`;
  },
  measurementSystems: "/measurement_systems",
  basemaps: "/basemaps",
  dateFormats: "/formats/date",
  timeFormats: "/formats/time"
};

export const ucEndpointsUrls = { ...ucEndpoints };
(Object.keys(ucEndpointsUrls) as (keyof typeof ucEndpointsUrls)[]).forEach(k => {
  ucEndpointsUrls[k] = ucAppserverBaseUrl + ucEndpoints[k];
});

export const amRoutes = {
  about: "/about",
  acceptInvitation: "/accept_invitation",
  accountCreate: "/account_create",
  accountUsers: "/account_users",
  confirmPasswordReset: "/confirm_password_reset",
  confirmRegistration: "/confirm_registration",
  dashboard: "/",
  help: "/help",
  login: "/login",
  management: "/management",
  managementPermissions: "/management/permissions",
  managementSubscription: "/management/subscription",
  managementSubscriptionAdd: "/management/subscription/add",
  managementUsers: "/management/users",
  managementRegions: "/management/regions",
  managementSettings: "/management/settings",
  managementSecurity: "/management/security",
  profile: "/profile",
  registration: "/registration"
} as const;

export const managementTabs = {
  subscription: "subscription",
  users: "users",
  permissions: "permissions",
  regions: "regions",
  settings: "settings",
  security: "security"
};

export const ucUserCreate = { system: "penciler", city: "San Francisco" };

export const adminRoles = ["PENCILER_ADMIN", "URBANCANVAS_ADMIN", "URBANSIM_ADMIN"];

export class RoleClass {
  displayName: string;
  code: string;
  inviteUserPopup: { header: string; content: React.ReactFragment };
  constructor(
    displayName: string,
    code: string,
    inviteUserPopup: { header: string; content: React.ReactFragment }
  ) {
    this.displayName = displayName;
    this.code = code;
    this.inviteUserPopup = inviteUserPopup;
  }
}

export const urbancanvasRoles = {
  prefix: "URBANCANVAS_",
  namePrefix: "UrbanCanvas ",
  canOnlyAddUsersTo: {
    URBANCANVAS_ADMIN: [
      "Admin",
      "Administrator",
      "URBANCANVAS_ADMIN",
      "ADMIN",
      "MODELER",
      "Viewer",
      "URBANCANVAS_MODELER",
      "URBANCANVAS_VIEWER",
      "MODELER",
      "VIEWER"
    ],
    URBANCANVAS_MODELER: ["Viewer", "URBANCANVAS_VIEWER", "VIEWER"],
    URBANCANVAS_VIEWER: [],
    URBANCANVAS_DEMO: []
  },
  availableRoles: [
    new RoleClass("UrbanCanvas Administrator", "URBANCANVAS_ADMIN", {
      header: "An administrator can: ",
      content: (
        <ul>
          <li>Assign roles to users associated with your account</li>
          <li>Invite Modelers and Viewers to join the account over email</li>
          <li>
            Configure what components of the user interface are visible for <br />
            Modelers and Viewers and read and write permissions
          </li>
          <li>Remove users from your account</li>
          <li>View and manage your subscription</li>
        </ul>
      )
    }),
    new RoleClass("UrbanCanvas Modeler", "URBANCANVAS_MODELER", {
      header: "Role permissions are configured by the account admin but by default: ",
      content: (
        <ul>
          <li>View, create, edit and delete map layers</li>
          <li>Upload files and view, edit and delete uploaded files</li>
          <li>View, create, edit and delete development projects, constraints and adjustments</li>
          <li>View, create, edit and delete scenarios</li>
          <li>View, create, edit and delete charts and dashboards</li>
          <li>View, create, edit and delete comments</li>
          <li>View, create, edit and delete indicators</li>
          <li>View and launch simulations</li>
        </ul>
      )
    }),
    new RoleClass("UrbanCanvas Viewer", "URBANCANVAS_VIEWER", {
      header: "Role permissions are configured by the account admin but by default: ",
      content: (
        <ul>
          <li>View map layers</li>
          <li>View development projects, constraints and adjustments</li>
          <li>View scenarios</li>
          <li>View charts and dashboards</li>
          <li>View, create, and edit comments</li>
          <li>View indicators</li>
        </ul>
      )
    })
  ],
  canManageSubscription: ["URBANCANVAS_ADMIN", "URBANCANVAS_DEMO"],
  canEditViewport: ["URBANCANVAS_ADMIN", "URBANCANVAS_MODELER"],
  canEditUsers: ["URBANCANVAS_ADMIN"],
  system: "urbancanvas",
  urbancanvasDemoCode: "URBANCANVAS_DEMO",
  colors: {
    URBANCANVAS_ADMIN: "blue",
    URBANCANVAS_MODELER: "teal",
    URBANCANVAS_VIEWER: "yellow",
    URBANCANVAS_DEMO: "brown"
  },
  defaultColor: "grey"
};

export const pencilerRoles = {
  prefix: "PENCILER_",
  namePrefix: "Penciler ",
  canOnlyAddUsersTo: {
    PENCILER_ADMIN: [
      "Admin",
      "Administrator",
      "PENCILER_ADMIN",
      "ADMIN",
      "Editor",
      "Viewer",
      "PENCILER_EDITOR",
      "PENCILER_VIEWER",
      "EDITOR",
      "VIEWER"
    ],
    PENCILER_EDITOR: ["Viewer", "PENCILER_VIEWER", "VIEWER"],
    PENCILER_VIEWER: [],
    PENCILER_DEMO: []
  },
  availableRoles: [
    new RoleClass("Penciler Administrator", "PENCILER_ADMIN", {
      header: "An administrator can: ",
      content: (
        <ul>
          <li>Upgrade account subscription</li>
          <li>Assign roles to every user associated with the account</li>
          <li>Invite both Editors and Viewers to join the account over email</li>
          <li>Add a new project</li>
          <li>Add a new building option in a project</li>
          <li>Edit any building option</li>
          <li>Delete any building option or project</li>
          <li>Remove users from the account</li>
        </ul>
      )
    }),
    new RoleClass("Penciler Editor", "PENCILER_EDITOR", {
      header: "An Editor can: ",
      content: (
        <ul>
          <li>Start projects</li>
          <li>Add building options in existing projects</li>
          <li>Edit building options in existing projects</li>
          <li>Delete building options and projects</li>
          <li>Invite Viewers to the account over email</li>
        </ul>
      )
    }),
    new RoleClass("Penciler Viewer", "PENCILER_VIEWER", {
      header: "A Viewer can: ",
      content: (
        <ul>
          <li>View projects, see the summary tables and click on building options</li>
          <li>Click through the wizard on any building option without making changes</li>
        </ul>
      )
    })
  ],
  canManageSubscription: ["PENCILER_ADMIN", "PENCILER_DEMO"],
  canEditViewport: ["PENCILER_ADMIN", "PENCILER_EDITOR"],
  canEditUsers: ["PENCILER_ADMIN"],
  system: "penciler",
  pencilerDemoCode: "PENCILER_DEMO",
  colors: {
    PENCILER_ADMIN: "blue",
    PENCILER_EDITOR: "teal",
    PENCILER_VIEWER: "yellow",
    PENCILER_DEMO: "brown"
  },
  defaultColor: "grey"
};

export const stripeConfig = {
  publicKey:
    process.env.REACT_APP_STRIPE_PUBLIC_KEY || "pk_test_Ki6ATi1pHxKC6byzCTtpaKtV009WqNtcwd",
  successUrl:
    process.env.REACT_APP_STRIPE_SUCCESS_URL ||
    `${window.location.protocol}//${window.location.hostname}${
      window.location.port ? ":" + window.location.port : ""
    }${publicUrl}`,
  cancelUrl:
    process.env.REACT_APP_STRIPE_CANCEL_URL ||
    `${window.location.protocol}//${window.location.hostname}${
      window.location.port ? ":" + window.location.port : ""
    }${publicUrl}`,
  startProjectProductReference:
    process.env.REACT_APP_START_PROJECT_PRODUCT_REFERENCE || "start_projects"
};

export const termsOfServiceLink =
  process.env.REACT_APP_TERMS_OF_SERVICE_LINK || `${publicUrl}/docs/tos.pdf`;

export const privacyPolicyLink =
  process.env.REACT_APP_PRIVACY_POLICY || `${publicUrl}/docs/privacy.pdf`;

export const externalLinks = {
  googleAuthenticator:
    process.env.REACT_APP_EXTERNAL_LINKS_GOOGLE_AUTHENTICATOR ||
    "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2",
  urbancanvas: process.env.REACT_APP_EXTERNAL_LINKS_UC || "https://cloud.urbansim.com/",
  penciler: process.env.REACT_APP_EXTERNAL_LINKS_PENCILER || "https://cloud.penciler.org/",
  support: process.env.REACT_APP_EXTERNAL_LINKS_SUPPORT || "https://urbansim.com/support",
  urbansim: process.env.REACT_APP_EXTERNAL_LINKS_URBANSIM || "https://urbansim.com/"
};

export const availableSystems = {
  urbancanvas: "urbancanvas",
  penciler: "penciler"
};

export const reCaptchaSiteKey =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? "6LeKRmkcAAAAABRdDpIn0wnFw45x5fTS7k43k6Cu";

export const viewerRoleConfigEnabledDBs = ["mpo08198800_bm2", "mpo42196501", "cmap", "brisbane"];
